export type ExportPreset = {
  id: string;
  label: string;
  width: number;
  height: number;
  fps: number;
};

export const PRESETS: ExportPreset[] = [
  { id: 'yt-1080', label: 'YouTube 1080p30', width: 1920, height: 1080, fps: 30 },
  { id: 'yt-720', label: 'YouTube 720p30', width: 1280, height: 720, fps: 30 },
  { id: 'square', label: 'Square 1080', width: 1080, height: 1080, fps: 30 },
  { id: 'vertical', label: 'Vertical 9:16', width: 1080, height: 1920, fps: 30 },
  { id: 'yt-1080-60', label: 'YouTube 1080p60', width: 1920, height: 1080, fps: 60 },
];

export type RecorderResult = {
  blob: Blob;
  mimeType: string;
  extension: string;
  durationMs: number;
};

function pickMimeType(): { mimeType: string; extension: string } {
  const candidates: Array<{ mimeType: string; extension: string }> = [
    { mimeType: 'video/mp4;codecs=avc1.42E01E,mp4a.40.2', extension: 'mp4' },
    { mimeType: 'video/mp4', extension: 'mp4' },
    { mimeType: 'video/webm;codecs=vp9,opus', extension: 'webm' },
    { mimeType: 'video/webm;codecs=vp8,opus', extension: 'webm' },
    { mimeType: 'video/webm', extension: 'webm' },
  ];
  for (const c of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(c.mimeType)) {
      return c;
    }
  }
  return { mimeType: '', extension: 'webm' };
}

export function supportedExportLabel(): string {
  const { mimeType, extension } = pickMimeType();
  if (typeof MediaRecorder === 'undefined') return 'MediaRecorder unsupported';
  if (!mimeType) return 'video/webm (default)';
  return `${extension.toUpperCase()} · ${mimeType.split(';')[0]}`;
}

/**
 * Canvas + optional audio → MediaRecorder.
 * Clones audio tracks so stopping the recorder never kills the AudioContext graph.
 */
export class CanvasRecorder {
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private mixed: MediaStream | null = null;
  /** Tracks we created/cloned and must stop ourselves. */
  private owned: MediaStreamTrack[] = [];
  private startedAt = 0;

  get recording(): boolean {
    return this.recorder?.state === 'recording' || this.recorder?.state === 'paused';
  }

  get elapsedMs(): number {
    if (!this.startedAt) return 0;
    return performance.now() - this.startedAt;
  }

  start(canvas: HTMLCanvasElement, audioStream: MediaStream | null, fps: number): void {
    if (this.recorder) throw new Error('Already recording');

    const vStream = canvas.captureStream(fps);
    const tracks: MediaStreamTrack[] = [];
    this.owned = [];

    for (const t of vStream.getVideoTracks()) {
      tracks.push(t);
      this.owned.push(t);
    }

    if (audioStream) {
      for (const t of audioStream.getAudioTracks()) {
        // Clone so recorder.stop() cannot end the live graph track.
        const cloned = t.clone();
        tracks.push(cloned);
        this.owned.push(cloned);
      }
    }

    this.mixed = new MediaStream(tracks);
    const { mimeType } = pickMimeType();
    const options: MediaRecorderOptions = {
      videoBitsPerSecond: 8_000_000,
      audioBitsPerSecond: 192_000,
    };
    if (mimeType) options.mimeType = mimeType;

    this.chunks = [];
    this.recorder = new MediaRecorder(this.mixed, options);
    this.recorder.ondataavailable = (ev) => {
      if (ev.data.size > 0) this.chunks.push(ev.data);
    };
    this.startedAt = performance.now();
    this.recorder.start(400);
  }

  stop(): Promise<RecorderResult> {
    const rec = this.recorder;
    if (!rec) return Promise.reject(new Error('Not recording'));

    const { mimeType, extension } = pickMimeType();
    const durationMs = this.elapsedMs;

    return new Promise<RecorderResult>((resolve, reject) => {
      rec.onerror = () => {
        this.cleanup();
        reject(new Error('MediaRecorder error'));
      };
      rec.onstop = () => {
        const type = rec.mimeType || mimeType || 'video/webm';
        const blob = new Blob(this.chunks, { type });
        this.cleanup();
        const ext = type.includes('mp4') ? 'mp4' : extension;
        resolve({ blob, mimeType: type, extension: ext, durationMs });
      };
      if (rec.state === 'inactive') {
        this.cleanup();
        reject(new Error('Recorder not active'));
        return;
      }
      try {
        if (rec.state === 'recording' || rec.state === 'paused') rec.stop();
      } catch (e) {
        this.cleanup();
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    });
  }

  private cleanup(): void {
    this.recorder = null;
    this.chunks = [];
    this.startedAt = 0;
    for (const t of this.owned) {
      try {
        t.stop();
      } catch {
        /* ignore */
      }
    }
    this.owned = [];
    this.mixed = null;
  }
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 4_000);
}

export function slugify(s: string): string {
  return (
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 48) || 'freemusiers-viz'
  );
}
